import {useSeoMeta, useHead} from '#app';
import type {UseSeoMetaInput, TitleTemplate} from "@unhead/vue";
import {stripHtml} from "../helpers/html-helpers";
import {isString} from "../helpers/string-helpers";

export type UseMetaProps = {
    title?: string,
    titleTemplate?: TitleTemplate,
    description?: string | undefined,
    image?: string,
    noIndex?: boolean,
    type?: 'website' | 'article' | 'book' | 'profile' | 'music.song' | 'music.album' | 'music.playlist' | 'music.radio_status' | 'video.movie' | 'video.episode' | 'video.tv_show' | 'video.other',
    canonical?: string, // The URL to the canonical page
    scripts?: string[] | {[key: string]: any}[], // Custom scripts
    meta?: {[key: string]: any}[], // Custom meta tags
}

function removeUndefinedValues(obj: {[key: string]: any}) {
    return Object.entries(obj).filter(([key, value]) => value !== undefined)
        .reduce((obj: {[key: string]: any}, [key, value]) => {
            obj[key] = value;
            return obj;
        }, {})
}

/**
 * A friendly wrapper around the useSeoMeta composable that is optimized for our common page use cases.
 */
export function useMeta(props: UseMetaProps) {
    const title = props.title ? stripHtml(props.title) : undefined;
    const description = props.description ? stripHtml(props.description) : undefined;

    const metaData: UseSeoMetaInput = {
        title: title,
        titleTemplate: props.titleTemplate,
        ogTitle: title,
        description: description,
        ogDescription: description,
        ogImageUrl: props.image,
        ogImageSecureUrl: props.image,
        ogType: props.type,
        robots: props.noIndex ? 'noindex' : undefined,
    };

    // We remove all 'undefined' values from the object before passing it to the useSeoMeta composable so we inherit the values from the parent page.
    useSeoMeta(removeUndefinedValues(metaData));

    const scripts: {}[] = [];
    if (props.scripts?.length) {
        props.scripts.forEach(script => {
            if (isString(script)) {
                scripts.push({src: script, type: 'text/javascript', async: true})
            } else {
                scripts.push(script)
            }
        });
    }

    if (scripts.length || props.canonical || props.meta?.length) {
        useHead({
            script: scripts.length ? scripts : undefined,
            meta: props.meta || undefined,
            link: props.canonical ? [{rel: 'canonical', href: props.canonical}] : undefined,
        })
    }
}